import React, { useCallback, useState } from 'react';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useDealerBanners, sendDealerCommand } from './useDealerBanners';
import Banner from './_partials/Banner';
import ComplexBanner from './_partials/ComplexBanner';
import Video from './_partials/Video';
import { DaOffer, Platform } from './types';

import s from './DealerAd.scss';

export interface Props {
  realEstateId: string;
  theme: string;
  width?: number;
  height?: number;
  userGuid: string;
  biClientId: string;
  biClientIdSigned: string;
  additionalParams?: { [s: string]: string }; // TODO remove if we dont use it
  withCloseButton: boolean;
  platform: Platform;
}

const DealerAd: React.FC<Props> = ({
  realEstateId,
  userGuid = '',
  biClientId,
  biClientIdSigned,
  platform,
}) => {
  const { isEmpty, isLoaded, offers, onCtaClick } = useDealerBanners(
    realEstateId,
    biClientIdSigned,
    userGuid === '' ? biClientId : userGuid
  );
  // When we tell parent to do something like show modal or adhesion
  const [chromeSignalSent, setChromeSignalSent] = useState(false);

  const ctaClick = useCallback(
    (url: string, o: DaOffer) => {
      const urlObj = new URL(url);
      urlObj.searchParams.append('dealer_off', '1');
      onCtaClick(o, urlObj.toString());
    },
    [onCtaClick]
  );

  if (isEmpty || (isLoaded && !offers.length)) {
    return null; // collapse
  }

  if (!offers.length) {
    return null;
  }

  // New-world offers with 'creative' content instead of 'payloadJson'
  // These we have to render/handle ourselves
  const theOffer = offers[0].asset;
  if (isLoaded && theOffer.creative) {
    const usedOffer = offers[0];
    const creative = theOffer.creative;
    switch (creative.creativeType) {
      case 'modal':
        if (!chromeSignalSent) {
          setChromeSignalSent(true);
          sendDealerCommand('show-modal', realEstateId, usedOffer);
        }

        const iframeContent =
          creative.modal?.[platform]?.iframeContent ??
          creative.modal?.desktop.iframeContent;
        let BannerEl;
        switch (iframeContent) {
          case 'banner':
            BannerEl = Banner;
            break;
          case 'videobanner':
            BannerEl = Video;
            break;
          case 'complexbanner':
          default:
            BannerEl = ComplexBanner;
        }
        const bannerData = creative[iframeContent ?? 'banner'];

        return (
          <ErrorBoundary debugComponent="DealerAd">
            <div className={s['root']}>
              <BannerEl
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={bannerData as any}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'banner':
        return (
          <ErrorBoundary debugComponent="DealerAdComplexBanner">
            <div className={s['root']}>
              <Banner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.banner!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'videobanner':
        return (
          <ErrorBoundary debugComponent="DealerAdVideoBanner">
            <div className={s['root']}>
              <Video
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.videobanner!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'adhesion':
        if (!chromeSignalSent) {
          setChromeSignalSent(true);
          sendDealerCommand('show-adhesion', realEstateId, usedOffer);
        }
        return (
          <ErrorBoundary debugComponent="DealerAd">
            <div className={s['root']}>
              <Banner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.adhesion!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );

      case 'complexbanner':
        return (
          <ErrorBoundary debugComponent="DealerAdComplexBanner">
            <div className={s['root']}>
              <ComplexBanner
                onCtaClick={url => ctaClick(url ?? '', usedOffer)}
                banner={creative.complexbanner!}
                platform={platform}
              />
            </div>
          </ErrorBoundary>
        );
      default:
    }
    return null;
  }
};
DealerAd.displayName = 'DealerAd';

// we just don't care for prop changes, period
export default React.memo(DealerAd, () => true);
